import React from 'react';
import {
  ArrowLeftRight,
  Check,
  ChevronDown,
  ChevronUp,
  Flag,
  Home,
  Search,
  Store,
  Sun,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { OfferVatMode } from '../../../types';
import { OFFER_VAT_OPTIONS } from '../utils/offerVat';

const STANDARD_ORDER: OfferVatMode[] = ['vat_19', 'vat_16', 'vat_7', 'vat_5'];

const SPECIAL_ORDER: OfferVatMode[] = [
  'vat_0_innergemeinschaftliche_lieferung',
  'vat_0_organschaft',
  'vat_0_photovoltaik',
  'vat_0_reverse_charge',
  'vat_0_kleinunternehmer',
  'vat_0_grundstuecksverkauf',
];

const SPECIAL_ICONS: Partial<Record<OfferVatMode, LucideIcon>> = {
  vat_0_innergemeinschaftliche_lieferung: Flag,
  vat_0_organschaft: Users,
  vat_0_photovoltaik: Sun,
  vat_0_reverse_charge: ArrowLeftRight,
  vat_0_kleinunternehmer: Store,
  vat_0_grundstuecksverkauf: Home,
};

function optionByMode(mode: OfferVatMode) {
  return OFFER_VAT_OPTIONS.find((o) => o.mode === mode)!;
}

function matchesSearch(label: string, q: string): boolean {
  if (!q.trim()) return true;
  const n = q.trim().toLowerCase();
  return label.toLowerCase().includes(n);
}

type OfferVatPickerPanelProps = {
  value: OfferVatMode;
  onChange: (mode: OfferVatMode) => void;
};

export const OfferVatPickerPanel: React.FC<OfferVatPickerPanelProps> = ({ value, onChange }) => {
  const [standardOpen, setStandardOpen] = React.useState(true);
  const [search, setSearch] = React.useState('');

  const standardOptions = STANDARD_ORDER.map(optionByMode).filter((o) => matchesSearch(o.label, search));
  const specialOptions = SPECIAL_ORDER.map(optionByMode).filter((o) => matchesSearch(o.label, search));

  const showStandardBlock = standardOptions.length > 0;
  const showSpecialBlock = specialOptions.length > 0;

  const rowSelected = (mode: OfferVatMode) => value === mode;

  return (
    <div className="w-full max-w-[440px] rounded-lg border border-app-border bg-app-surface shadow-md overflow-hidden text-left">
      {showStandardBlock && (
        <div className="border-b border-app-border">
          <button
            type="button"
            onClick={() => setStandardOpen((o) => !o)}
            className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[11px] font-semibold text-neutral-700 hover:bg-app-hover/80"
          >
            <span>Standard-USt.-Sätze</span>
            {standardOpen ? (
              <ChevronUp className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
            )}
          </button>
          {standardOpen && (
            <ul>
              {standardOptions.map((opt) => {
                const sel = rowSelected(opt.mode);
                return (
                  <li key={opt.mode}>
                    <button
                      type="button"
                      onClick={() => onChange(opt.mode)}
                      className={`flex w-full items-center gap-1.5 px-3 py-1 text-left text-[11px] transition-colors ${
                        sel ? 'bg-sky-50 text-sky-900' : 'text-neutral-700 hover:bg-app-hover'
                      }`}
                    >
                      <span className="flex w-4 shrink-0 justify-center">
                        {sel ? <Check className="h-3 w-3 text-sky-600" strokeWidth={2.5} /> : null}
                      </span>
                      <span>{opt.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {showSpecialBlock && (
        <div className="border-b border-app-border">
          <div className="px-3 py-1.5 text-[11px] font-semibold text-neutral-700">Ausnahmen &amp; Sonderregelungen</div>
          <ul>
            {specialOptions.map((opt) => {
              const sel = rowSelected(opt.mode);
              const Icon = SPECIAL_ICONS[opt.mode] ?? Flag;
              return (
                <li key={opt.mode}>
                  <button
                    type="button"
                    onClick={() => onChange(opt.mode)}
                    className={`flex w-full items-center gap-2 px-3 py-1 text-left text-[11px] transition-colors ${
                      sel ? 'bg-sky-50 text-sky-900' : 'text-neutral-700 hover:bg-app-hover'
                    }`}
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center text-neutral-400">
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1 leading-snug">{opt.label}</span>
                    {sel && <Check className="h-3 w-3 shrink-0 text-sky-600" strokeWidth={2.5} />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!showStandardBlock && !showSpecialBlock && (
        <div className="px-3 py-4 text-center text-[11px] text-neutral-500">Kein Treffer für diese Suche.</div>
      )}

      <div className="flex items-center gap-2 border-t border-app-border bg-app-surface-muted/60 px-3 py-1.5">
        <span className="shrink-0 text-[10px] text-neutral-500 whitespace-nowrap">USt.-Satz suchen:</span>
        <span className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-neutral-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="z. B. Photovoltaik"
            className="w-full rounded border border-app-border bg-app-surface py-1 pl-6 pr-2 text-[11px] text-neutral-900 placeholder:text-neutral-400 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-300"
          />
        </span>
      </div>
    </div>
  );
};
